import {
  prisma,
  type Prisma,
  type EntityTier,
  type EntityTierSettings,
  BookableItemType,
} from "client";
import { createBookableItem, updateBookableItem } from "../bookableItem";

// ============================================================================
// CREATE
// ============================================================================

export interface CreateEntityTierInput {
  floorPlanId?: string;
  eventId: string;
  name: string;
  isDefault?: boolean;
  bookableItem: Omit<Prisma.BookableItemCreateInput, "type" | "event">;
  settings: Prisma.EntityTierSettingsCreateInput;
}

/**
 * Create a new EntityTier with its BookableItem
 * Validates that only one default tier exists per floor plan
 */
export const createEntityTier = async (data: CreateEntityTierInput) => {
  try {
    return await prisma.$transaction(async (tx) => {
      // Validate: Only one default tier per floor plan
      if (data.isDefault === true) {
        const existingDefault = await tx.entityTier.findFirst({
          where: {
            floorPlanId: data.floorPlanId,
            isDefault: true,
            OR: [{ deletedAt: null }, { deletedAt: { isSet: false } }],
          },
          select: { id: true },
        });

        if (existingDefault) {
          throw new Error(
            "A default EntityTier already exists for this floor plan. Please unset the current default first."
          );
        }
      }

      // Create the BookableItem first
      const bookableItem = await createBookableItem(
        {
          type: BookableItemType.ENTITY_TIER,
          event: {
            connect: { id: data.eventId },
          },
          price: 0,
          minAmount: 1,
          soldTogether: 1,
          // the admin should make it false
          showWhenSoldOut: true,
          ...data.bookableItem,
        },
        tx
      );

      // Create the EntityTier linked to the BookableItem
      const entityTier = await tx.entityTier.create({
        data: {
          floorPlan: {
            connect: { id: data.floorPlanId },
          },
          bookableItem: {
            connect: { id: bookableItem.id },
          },
          name: data.name,
          isDefault: data.isDefault ?? false,
          event: {
            connect: { id: data.eventId },
          },
          settings: data.settings,
        },
        include: {
          bookableItem: {
            include: {
              translations: true,
              prices: true,
            },
          },
        },
      });

      return entityTier;
    });
  } catch (error: any) {
    throw new Error(`Failed to create EntityTier: ${error.message}`);
  }
};

// ============================================================================
// READ
// ============================================================================

/**
 * Get a single EntityTier with optional select/include
 * @overload - With select
 */
export async function getEntityTierWithInclude(
  id: string,
  include: Prisma.EntityTierInclude
) {
  try {
    return await prisma.entityTier.findUnique({ where: { id }, include });
  } catch (error: any) {
    throw new Error(`Failed to get EntityTier: ${error.message}`);
  }
}

export async function getEntityTierWithSelect(
  id: string,
  select: Prisma.EntityTierSelect
) {
  try {
    return await prisma.entityTier.findUnique({ where: { id }, select });
  } catch (error: any) {
    throw new Error(`Failed to get EntityTier: ${error.message}`);
  }
}

export async function getEntityTier(id: string) {
  try {
    return await prisma.entityTier.findUnique({ where: { id } });
  } catch (error: any) {
    throw new Error(`Failed to get EntityTier: ${error.message}`);
  }
}

/**
 * get EntityTiers by FloorPlan
 */
const getEntityTierWhere = (floorPlanId: string) => {
  const where: Prisma.EntityTierWhereInput = {
    AND: [
      {
        OR: [
          { deletedAt: null, floorPlanId },
          { deletedAt: { isSet: false }, floorPlanId },
        ],
      },
      {
        floorPlanId,
      },
    ],
  };
  return where;
};

const entityTierOrderBy = [
  { isDefault: "desc" as const },
  { createdAt: "asc" as const },
];

export async function getEntityTiersByFloorPlanWithInclude(
  floorPlanId: string,
  include: Prisma.EntityTierInclude
) {
  try {
    const where = getEntityTierWhere(floorPlanId);
    return await prisma.entityTier.findMany({
      where,
      include,
      orderBy: entityTierOrderBy,
    });
  } catch (error: any) {
    throw new Error(`Failed to get EntityTiers by FloorPlan: ${error.message}`);
  }
}

export async function getEntityTiersByFloorPlanWithSelect(
  floorPlanId: string,
  select: Prisma.EntityTierSelect
) {
  try {
    const where = getEntityTierWhere(floorPlanId);
    return await prisma.entityTier.findMany({
      where,
      select,
      orderBy: entityTierOrderBy,
    });
  } catch (error: any) {
    throw new Error(`Failed to get EntityTiers by FloorPlan: ${error.message}`);
  }
}

export async function getEntityTiersByFloorPlan(floorPlanId: string) {
  try {
    const where = getEntityTierWhere(floorPlanId);

    return await prisma.entityTier.findMany({
      where,
      orderBy: entityTierOrderBy,
    });
  } catch (error: any) {
    throw new Error(`Failed to get EntityTiers by FloorPlan: ${error.message}`);
  }
}

/**
 * Get the default EntityTier for a FloorPlan
 */
export const getDefaultEntityTier = async (
  floorPlanId: string
): Promise<EntityTier | null> => {
  try {
    const defaultTier = await prisma.entityTier.findFirst({
      where: {
        floorPlanId,
        isDefault: true,
        OR: [{ deletedAt: null }, { deletedAt: { isSet: false } }],
      },
      include: {
        bookableItem: {
          include: {
            translations: true,
            designs: true,
          },
        },
      },
    });
    return defaultTier;
  } catch (error: any) {
    throw new Error(`Failed to get default EntityTier: ${error.message}`);
  }
};

/**
 * Get EntityTier by FloorPlan and name
 */
export const getEntityTierByName = async (
  floorPlanId: string,
  name: string
): Promise<EntityTier | null> => {
  try {
    const entityTier = await prisma.entityTier.findUnique({
      where: {
        floorPlanId_name: {
          floorPlanId,
          name,
        },
      },
      include: {
        bookableItem: {
          include: {
            translations: true,
            designs: true,
          },
        },
      },
    });
    return entityTier;
  } catch (error: any) {
    throw new Error(`Failed to get EntityTier by name: ${error.message}`);
  }
};

export async function getEntityTiersByEventId(eventId: string) {
  try {
    return await prisma.entityTier.findMany({
      where: { eventId },
    });
  } catch (error: any) {
    throw new Error(`Failed to get EntityTiers by Event: ${error.message}`);
  }
}

// ============================================================================
// UPDATE
// ============================================================================

export interface UpdateEntityTierInput {
  name?: string;
  isDefault?: boolean;
  bookableItem?: Prisma.BookableItemUpdateInput;
  settings?: Partial<EntityTierSettings>;
}

/**
 * Update an EntityTier and optionally its BookableItem
 * Uses transaction to ensure atomicity
 */
export const updateEntityTier = async (
  id: string,
  data: UpdateEntityTierInput
): Promise<EntityTier> => {
  try {
    return await prisma.$transaction(async (tx) => {
      // Get the current tier with minimal data
      const currentTier = await tx.entityTier.findUnique({
        where: { id },
        select: {
          id: true,
          floorPlanId: true,
          bookableItemId: true,
          isDefault: true,
        },
      });

      if (!currentTier) {
        throw new Error("EntityTier not found");
      }
      const updateData: Prisma.EntityTierUpdateInput = {};

      // If updating isDefault to true
      if (
        data.isDefault === true &&
        !currentTier.isDefault &&
        currentTier.floorPlanId
      ) {
        // Unset other defaults in the same floor plan
        await tx.entityTier.updateMany({
          where: {
            isDefault: true,
            floorPlanId: currentTier.floorPlanId,
          },
          data: { isDefault: false },
        });

        // Update floor plan's defaultEntityTierId
        updateData.isDefault = true;
      }

      // Update BookableItem if data provided
      if (data.bookableItem) {
        await updateBookableItem(
          currentTier.bookableItemId,
          data.bookableItem,
          tx
        );
      }

      // Update EntityTier-specific fields
      if (data.name !== undefined) updateData.name = data.name;
      if (data.isDefault !== undefined) updateData.isDefault = data.isDefault;
      if (data.settings) updateData.settings = data.settings;

      const entityTier = await tx.entityTier.update({
        where: { id },
        data: updateData,
        include: {
          bookableItem: {
            include: {
              translations: true,
              prices: true,
            },
          },
        },
      });

      return entityTier;
    });
  } catch (error: any) {
    throw new Error(`Failed to update EntityTier: ${error.message}`);
  }
};

/**
 * Set a tier as the default for its floor plan
 */
export const setDefaultEntityTier = async (
  floorPlanId: string,
  tierId: string
): Promise<EntityTier> => {
  try {
    return await prisma.$transaction(async (tx) => {
      const existingDefault = await tx.entityTier.findFirst({
        where: {
          floorPlanId,
          isDefault: true,
        },
      });
      if (existingDefault) {
        await tx.entityTier.update({
          where: { id: existingDefault.id },
          data: {
            isDefault: false,
          },
        });
      }

      // Set the new default
      const entityTier = await tx.entityTier.update({
        where: { id: tierId },
        data: { isDefault: true },
        include: {
          bookableItem: true,
        },
      });

      return entityTier;
    });
  } catch (error: any) {
    console.error("Error setting default EntityTier:", error);
    throw new Error(`Failed to set default EntityTier: ${error.message}`);
  }
};

// ============================================================================
// DELETE
// ============================================================================

/**
 * Soft delete an EntityTier
 * Optionally migrate entities to another tier before deletion
 * Uses transaction to ensure atomicity
 *
 * @param id - EntityTier ID to delete
 * @param targetTierId - Optional tier ID to migrate entities to. If not provided, will fail if entities exist.
 */
export const deleteEntityTier = async (id: string, targetTierId?: string) => {
  try {
    return await prisma.$transaction(async (tx) => {
      const now = new Date();

      // Get tier with minimal entity data
      const tier = await tx.entityTier.findUnique({
        where: { id },
        select: {
          id: true,
          bookableItemId: true,
          deletedAt: true,
          _count: {
            select: { entities: true },
          },
        },
      });

      if (!tier) {
        throw new Error("EntityTier not found");
      }

      if (tier.deletedAt) {
        throw new Error("EntityTier is already deleted");
      }

      const entityCount = tier._count.entities;

      // Handle entities
      if (entityCount > 0) {
        if (targetTierId) {
          // Migrate entities to target tier
          await tx.entity.updateMany({
            where: { entityTierId: id },
            data: { entityTierId: targetTierId },
          });
        } else {
          throw new Error(
            `Cannot delete EntityTier: ${entityCount} entities are using this tier. ` +
              `Please provide targetTierId to migrate entities, or reassign them manually.`
          );
        }
      }

      // Soft delete EntityTier
      const deletedTier = await tx.entityTier.update({
        where: { id },
        data: { deletedAt: now },
      });

      // Soft delete associated BookableItem
      await tx.bookableItem.update({
        where: { id: tier.bookableItemId },
        data: { deletedAt: now },
      });

      return deletedTier;
    });
  } catch (error: any) {
    throw new Error(`Failed to delete EntityTier: ${error.message}`);
  }
};

// ============================================================================
// UTILITY
// ============================================================================

/**
 * Check if a tier name is available for a floor plan
 */
export const isEntityTierNameAvailable = async (
  floorPlanId: string,
  name: string,
  excludeTierId?: string
): Promise<boolean> => {
  try {
    const existing = await prisma.entityTier.findUnique({
      where: {
        floorPlanId_name: {
          floorPlanId,
          name,
        },
      },
      select: { id: true },
    });

    if (!existing) return true;
    if (excludeTierId && existing.id === excludeTierId) return true;

    return false;
  } catch (error: any) {
    return false;
  }
};

/**
 * Count bookable units in a tier.
 *
 * Booths count as 1 each. A SEATS_SECTION counts as its derived seat total
 * (Σ rowsConfig.seatCount), not 1 — otherwise a 500-seat section would look
 * like a single booth against tier quantity.
 */
export const countEntitiesInTier = async (tierId: string): Promise<number> => {
  try {
    const entities = await prisma.entity.findMany({
      where: { entityTierId: tierId },
      select: { type: true, seatSection: true },
    });
    let total = 0;
    for (const entity of entities) {
      if (entity.type === "SEATS_SECTION" && entity.seatSection) {
        const { rowsConfig, rows, seatsPerRow } = entity.seatSection;
        if (rowsConfig?.length) {
          for (const row of rowsConfig) {
            total += row.seatCount - (row.removedSeats?.length ?? 0);
          }
        } else {
          total += rows * seatsPerRow;
        }
      } else if (entity.type !== "SEATS_ROW" && entity.type !== "SEAT") {
        // Ignore leftover v1 row/seat entity rows if any still exist.
        total += 1;
      }
    }
    return total;
  } catch (error: any) {
    throw new Error(`Failed to count entities in tier: ${error.message}`);
  }
};
